import { Component, ElementRef, HostListener, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { SocialChartDetailsDialogService } from '../../../services/social-chart-details-dialog.service';
import { SocialSeriesService } from '../../../services/social-series.service';
import { ThemeAnalyticsChartOptionsService } from '../theme-analytics-chart-options.service';
import { ThemeAnalyticsChartSeriesService } from '../theme-analytics-chart-series.service';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { SocialMetricAnalysisTypesEnum } from '../../../../../Common/Charts/enums/social-analysis-types.enum';
import { MentionSearchModel } from '../../../../../Monitor/Shared/models/mention-search.model';
import { ThemeService } from '../../../services/theme.service';
import { DropdownTypesEnum } from '../../../../../Common/Forms/enums/dropdown-types.enum';
import { ChartModel } from '../../../../../Common/Charts/models/chart.model';
import { MentionSearchFacetTypesEnum } from '../../../../../api/mentions/enum/mention-search-facet-types.enum';
import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { NetworkTypeEnum } from '../../../../Core/enums/network-type.enum';
import { TopTermsDataMappers } from '@Social/Shared/components/TopTerms/top-terms-data-mappers';
import { mentionsByImpressionsNeoFacetRequest } from '../../../models/neo-facet-requests';
import { SocialArchiveSearchService } from '../../../services/social-archive-search.service';
import { SocialRoutingService } from '../../../../Core/services/social-routing.service';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';

@Component({
    selector: 'agpr-theme-analytics-trends',
    templateUrl: './theme-analytics-trends.component.html',
    styleUrls: ['./theme-analytics-trends.component.scss'],
    standalone: false
})
export class ThemeAnalyticsTrendsComponent implements OnInit, OnDestroy {
    readonly dropdownTypes = DropdownTypesEnum;
    readonly mentionSearchFacetTypesEnum = MentionSearchFacetTypesEnum;
    readonly searchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum;
    readonly socialMetricAnalysisTypes = SocialMetricAnalysisTypesEnum;
    readonly mostUsedTopTermsConfig = {
        mainTitle: {
            mode: 'single',
            title: 'Social.intelligence.charts.topTerms.heading.mostUsed',
        },
        subTitle: {
            mode: 'static',
            title: 'Social.intelligence.charts.topTerms.heading.mostUsedTerms'
        }
    };

    readonly mostProminentTopTermsConfig = {
        mainTitle: {
            mode: 'single',
            title: 'Social.intelligence.charts.topTerms.heading.mostProminent',
        },
        subTitle: {
            mode: 'static',
            title: 'Social.intelligence.charts.topTerms.heading.mostProminentTerms'
        }
    };

    // Top Posts
    public topPostsSeries: ReportSeriesModel;
    public topPostsSortField: SearchCriteriaSortFieldEnum;
    // Top Terms
    public mostUsedTopTermsSeries: ReportSeriesModel;
    public mostUsedTopTermsSortField: SearchCriteriaSortFieldEnum;
    public mostProminentTopTermsSeries: ReportSeriesModel;
    public mostProminentTopTermsSortField: SearchCriteriaSortFieldEnum;

    public isArchiveSearch: boolean = false;
    public enableEngagementInsights: boolean = false;

    idToChartModelMap: Map<string, ChartModel> = new Map<string, ChartModel>();

    public enabledNetworkTypes: NetworkTypeEnum[] = [
        NetworkTypeEnum.all,
        NetworkTypeEnum.twitter,
        NetworkTypeEnum.facebook,
        NetworkTypeEnum.instagram,
        NetworkTypeEnum.tiktok,
        NetworkTypeEnum.youtube,
        NetworkTypeEnum.reddit,
        NetworkTypeEnum.linkedin
    ];

    @Input() id: string;

    @ViewChild('sentimentSummaryChartLegend', {read: ElementRef, static: true}) sentimentSummaryChartLegend: ElementRef;

    private subscriptions: Subscription[] = [];

    constructor(
        public localizationService: LocalizationService,
        public themeAnalyticsChartSeriesService: ThemeAnalyticsChartSeriesService,
        public themeAnalyticsChartOptionsService: ThemeAnalyticsChartOptionsService,
        public socialSeriesService: SocialSeriesService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public coreDialogService: CoreDialogService,
        public injector: Injector,
        protected themeService: ThemeService,
        private socialRoutingService: SocialRoutingService,
        private socialArchiveSearchService: SocialArchiveSearchService
    ) {}

    @HostListener('click', ['$event.target'])
    onClick(event){
        if (event?.classList.contains('viewMentionLink') && event?.classList.length > 1) {
            const chartClass = event.classList[1];
            const chartModel = this.idToChartModelMap.get(chartClass);

            if (chartModel) {
                this.socialChartDetailsDialogService.openDrillDownPage(
                    event.href,
                    chartModel?.clone ? chartModel.clone() : chartModel
                );
            }
            return false;
        }
    }

    ngOnInit(): void {
        this.initializeAll();

        this.socialSeriesService.fulfillSeries(
            [
                // Top posts
                this.topPostsSeries,
                this.mostUsedTopTermsSeries,
                this.mostProminentTopTermsSeries
            ]
        );

        this.subscriptions.push(
            this.socialSeriesService.availableNetworks$.subscribe(availableNetworks => this.enabledNetworkTypes = availableNetworks),
            this.socialRoutingService.isArchiveSearch$.subscribe(x => this.isArchiveSearch = x),
            this.socialArchiveSearchService.enableEngagementInsights$.subscribe(x => this.enableEngagementInsights = x)
        );
    }

    ngOnDestroy(): void {
        this.deregisterSeries();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private initializeAll(): void {
        this.initializeTopPosts();
        this.initializeTopTerms();
    }

    private deregisterSeries(): void {
        this.socialSeriesService.deregisterSeries([
            // Top posts
            this.topPostsSeries,
            this.mostUsedTopTermsSeries,
            this.mostProminentTopTermsSeries
        ]);
    }

    public initializeTopPosts(): void {
        this.topPostsSeries = new ReportSeriesModel();
        this.topPostsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.topPostsSortField = SearchCriteriaSortFieldEnum.engagement_total;
        this.topPostsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: SearchCriteriaSortFieldEnum.engagement_total,
                pageSize: 5
            };
        };
        this.topPostsSeries.mentions.mentionDataMapper = (response) => {
            return response.mentions.map(apiMention => new Mention(apiMention));
        };
        this.topPostsSeries.mentions.filters = new MentionSearchModel();
    }

    public initializeTopTerms(): void {
        const facetGenerator = () => {
            return {
                facet: [
                    // Initial Facet hashtags
                    MentionSearchFacetTypesEnum.hashtags,
                ],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };

        this.mostUsedTopTermsSeries = new ReportSeriesModel();
        this.mostUsedTopTermsSeries.mentions.facetGenerator = facetGenerator;
        this.mostUsedTopTermsSeries.mentions.dataMapper = TopTermsDataMappers.hashtagDataMapper(this.mostUsedTopTermsSeries);

        this.mostProminentTopTermsSeries = new ReportSeriesModel();
        this.mostProminentTopTermsSeries.mentions.facetGenerator = () => {
            return {
                facet: mentionsByImpressionsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.mostProminentTopTermsSeries.mentions.filters = new MentionSearchModel();
        this.mostProminentTopTermsSeries.mentions.dataMapper = TopTermsDataMappers.neofacetImpressionsDataMapper(this.mostProminentTopTermsSeries, true);
    }
}
